// src/api/centralAuthApi.ts
// Central Identity API endpoints using RTK Query
//
// IMPORTANT: Calls Central OIDC endpoints DIRECTLY (not proxied).
// Central is the sole authority for authentication.

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { decodeJwtPayload } from '@levelup/central-auth';
import { CENTRAL_URL, OIDC_ENDPOINTS } from '../config/centralAuth';
import type { RootState } from '../store/store';

// ============================================================
// Types
// ============================================================

/**
 * Central's 4 authoritative roles.
 */
export type CentralRole = 'owner' | 'admin' | 'member' | 'viewer';

/**
 * Workspace information from Central.
 */
export interface CentralWorkspace {
  id: string;
  name: string;
  slug: string;
  type: 'personal' | 'business';
  role: CentralRole;
  isPrimary: boolean;
}

/**
 * User information from Central.
 */
export interface CentralUser {
  id: string;
  email: string;
  fullName: string | null;
  avatarUrl: string | null;
  centralAdmin: boolean;
  workspaces: CentralWorkspace[];
  selectedWorkspaceId: string | null;
}

/**
 * Auth response containing tokens.
 */
export interface CentralAuthResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  user: CentralUser;
}

/**
 * Login request payload.
 */
export interface CentralLoginRequest {
  email: string;
  password: string;
}

/**
 * Workspace selection request.
 */
export interface CentralSelectWorkspaceRequest {
  workspaceId: string;
}

/**
 * Select workspace response with new token.
 */
export interface CentralSelectWorkspaceResponse {
  accessToken: string;
  expiresIn: number;
  workspace: CentralWorkspace;
}

// ============================================================
// Central Auth API - Direct OIDC Calls
// ============================================================

/**
 * RTK Query API for Central Identity OIDC endpoints.
 * Calls Central directly - no backend proxy.
 *
 * Uses credentials: 'include' for session cookie management.
 */
export const centralAuthApi = createApi({
  reducerPath: 'centralAuthApi',
  baseQuery: fetchBaseQuery({
    // Base URL is Central's OIDC session endpoint
    baseUrl: `${CENTRAL_URL}/api/oidc/session`,
    // Include credentials for session cookies
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      // Get Central access token from state (for authenticated requests)
      const token = (getState() as RootState).centralAuth?.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['CentralUser', 'CentralWorkspace'],
  endpoints: (builder) => ({
    /**
     * Login via Central Identity OIDC session endpoint.
     * POST /api/oidc/session/login
     */
    centralLogin: builder.mutation<CentralAuthResponse, CentralLoginRequest>({
      query: (credentials) => ({
        url: '/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['CentralUser'],
    }),

    /**
     * Logout via Central Identity.
     * POST /api/oidc/session/logout
     */
    centralLogout: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: '/logout',
        method: 'POST',
      }),
      invalidatesTags: ['CentralUser', 'CentralWorkspace'],
    }),

    /**
     * Get list of workspaces the user belongs to.
     * GET /api/oidc/session/workspaces
     */
    getCentralWorkspaces: builder.query<{ workspaces: CentralWorkspace[] }, void>({
      query: () => '/workspaces',
      providesTags: ['CentralWorkspace'],
    }),

    /**
     * Select/switch workspace.
     * POST /api/oidc/session/select-workspace
     * Returns a new access token with the selected workspace embedded.
     */
    centralSelectWorkspace: builder.mutation<
      CentralSelectWorkspaceResponse,
      CentralSelectWorkspaceRequest
    >({
      query: (body) => ({
        url: '/select-workspace',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CentralWorkspace'],
    }),

    /**
     * Refresh access token using refresh token.
     * POST /api/oidc/session/refresh
     */
    centralRefresh: builder.mutation<
      { accessToken: string; expiresIn: number },
      { refreshToken: string }
    >({
      query: (body) => ({
        url: '/refresh',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Create a new workspace via Central.
     * POST /api/oidc/session/workspaces
     */
    createCentralWorkspace: builder.mutation<
      { workspace: CentralWorkspace },
      { name: string; type: string; industry?: string }
    >({
      query: (body) => ({
        url: '/workspaces',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CentralWorkspace'],
    }),
  }),
});

// Export hooks for use in components
export const {
  useCentralLoginMutation,
  useCentralLogoutMutation,
  useGetCentralWorkspacesQuery,
  useLazyGetCentralWorkspacesQuery,
  useCentralSelectWorkspaceMutation,
  useCentralRefreshMutation,
  useCreateCentralWorkspaceMutation,
} = centralAuthApi;

// ============================================================
// PKCE Flow Utilities (for mobile/native apps)
// ============================================================

/**
 * SHA-256 digest with fallback for insecure (HTTP) origins.
 * crypto.subtle is only available in secure contexts (HTTPS / localhost).
 * On plain HTTP we fall back to a JS implementation so PKCE still works
 * on staging servers that don't have TLS configured yet.
 */
async function sha256(data: Uint8Array): Promise<ArrayBuffer> {
  if (crypto.subtle) {
    return crypto.subtle.digest('SHA-256', data as unknown as BufferSource);
  }

  // Minimal SHA-256 fallback (RFC 6234) for insecure origins.
  const K = new Uint32Array([
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
  ]);

  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  const ch   = (x: number, y: number, z: number) => (x & y) ^ (~x & z);
  const maj  = (x: number, y: number, z: number) => (x & y) ^ (x & z) ^ (y & z);
  const sig0 = (x: number) => rotr(x, 2) ^ rotr(x, 13) ^ rotr(x, 22);
  const sig1 = (x: number) => rotr(x, 6) ^ rotr(x, 11) ^ rotr(x, 25);
  const gam0 = (x: number) => rotr(x, 7) ^ rotr(x, 18) ^ (x >>> 3);
  const gam1 = (x: number) => rotr(x, 17) ^ rotr(x, 19) ^ (x >>> 10);

  // Pre-processing: padding
  // Total length must be a multiple of 64 bytes (512 bits).
  // Append 0x80, then zero-pad, then 8-byte big-endian bit length.
  const msgLen = data.length;
  const bitLen = msgLen * 8;
  // Need: msgLen + 1 (0x80) + padding + 8 (length) ≡ 0 (mod 64)
  const totalWithoutPad = msgLen + 1 + 8; // message + 0x80 + 8-byte length
  const paddedLen = Math.ceil(totalWithoutPad / 64) * 64;
  const padded = new Uint8Array(paddedLen);
  padded.set(data);
  padded[msgLen] = 0x80;
  // Remaining bytes are already 0 (Uint8Array is zero-initialized)
  const view = new DataView(padded.buffer);
  // Store 64-bit message length in bits at the end (big-endian)
  // For messages < 512 MB, high 32 bits are 0
  view.setUint32(padded.length - 8, 0, false);
  view.setUint32(padded.length - 4, bitLen, false);

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  const W = new Uint32Array(64);
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i++) W[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i++) W[i] = (gam1(W[i-2]) + W[i-7] + gam0(W[i-15]) + W[i-16]) | 0;

    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let i = 0; i < 64; i++) {
      const t1 = (h + sig1(e) + ch(e, f, g) + K[i] + W[i]) | 0;
      const t2 = (sig0(a) + maj(a, b, c)) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0;
      d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g) | 0; h7 = (h7 + h) | 0;
  }

  const result = new ArrayBuffer(32);
  const out = new DataView(result);
  out.setUint32(0, h0); out.setUint32(4, h1); out.setUint32(8, h2); out.setUint32(12, h3);
  out.setUint32(16, h4); out.setUint32(20, h5); out.setUint32(24, h6); out.setUint32(28, h7);
  return result;
}

/**
 * Generate PKCE code verifier and challenge.
 * Used for Authorization Code + PKCE flow.
 */
export async function generatePKCE(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  // Generate random code verifier (43-128 chars)
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const codeVerifier = base64UrlEncode(array);

  // Generate code challenge (S256) — uses fallback on insecure origins
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await sha256(data);
  const codeChallenge = base64UrlEncode(new Uint8Array(hash));

  return { codeVerifier, codeChallenge };
}

/**
 * Base64 URL encode (no padding).
 */
function base64UrlEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Build the OIDC authorization URL for PKCE flow.
 */
export function buildAuthorizationUrl(params: {
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
  state: string;
  scope?: string;
}): string {
  const url = new URL(OIDC_ENDPOINTS.authorize);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', params.clientId);
  url.searchParams.set('redirect_uri', params.redirectUri);
  url.searchParams.set('code_challenge', params.codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', params.state);
  if (params.scope) {
    url.searchParams.set('scope', params.scope);
  }
  return url.toString();
}

/**
 * Standard OIDC token response (snake_case).
 * Central's /api/oidc/token endpoint may return this format.
 */
interface OIDCTokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type?: string;
  expires_in: number;
}


/**
 * Exchange authorization code for tokens.
 *
 * Handles both standard OIDC response format (snake_case) and
 * Central's custom format (camelCase with user object).
 * When the response doesn't include a user object, constructs one
 * from JWT claims and fetches workspaces from the session endpoint.
 */
export async function exchangeCodeForTokens(params: {
  code: string;
  codeVerifier: string;
  clientId: string;
  redirectUri: string;
}): Promise<CentralAuthResponse> {
  const response = await fetch(OIDC_ENDPOINTS.token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code: params.code,
      code_verifier: params.codeVerifier,
      client_id: params.clientId,
      redirect_uri: params.redirectUri,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Token exchange failed' }));
    throw new Error(error.error_description || error.error || 'Token exchange failed');
  }

  const data = await response.json();

  // If response already has camelCase accessToken and user, return as-is
  if (data.accessToken && data.user) {
    return data as CentralAuthResponse;
  }

  // Standard OIDC response (snake_case) — normalize to CentralAuthResponse
  const oidc = data as OIDCTokenResponse;
  const accessToken = oidc.access_token || data.accessToken;
  const refreshToken = oidc.refresh_token || data.refreshToken;
  const expiresIn = oidc.expires_in ?? data.expiresIn ?? 3600;

  if (!accessToken) {
    throw new Error('No access token in response');
  }

  // Extract user info from JWT claims only — no invented data.
  // BootGate's GET /api/suite/switch-context will hydrate the full
  // workspace list, apps, and user profile from Central.
  let user: CentralUser;
  try {
    const claims = decodeJwtPayload(accessToken);

    user = {
      id: (claims.sub as string) || '',
      email: (claims.email as string) || '',
      fullName: (claims.name as string) || null,
      avatarUrl: null,
      centralAdmin: Boolean(claims.centralAdmin),
      workspaces: [],    // BootGate will populate from switch-context
      selectedWorkspaceId: (claims.workspaceId as string) || null,
    };
  } catch {
    throw new Error('Failed to decode user information from access token');
  }

  return { accessToken, refreshToken, expiresIn, user };
}
